import { createSignal, onMount } from 'solid-js';

export interface StorageInfo {
  supported: () => boolean;
  quotaBytes: () => number | null;
  usageBytes: () => number | null;
  persisted: () => boolean | null;
  lastError: () => string | null;
  refresh: () => Promise<void>;
  requestPersistent: () => Promise<boolean>;
}

/** Reactive view onto `navigator.storage` estimate + persisted state. */
export function createStorageInfo(): StorageInfo {
  const supported = () =>
    typeof navigator !== 'undefined' && 'storage' in navigator && typeof navigator.storage.estimate === 'function';
  const [quotaBytes, setQuotaBytes] = createSignal<number | null>(null);
  const [usageBytes, setUsageBytes] = createSignal<number | null>(null);
  const [persisted, setPersisted] = createSignal<boolean | null>(null);
  const [lastError, setLastError] = createSignal<string | null>(null);

  const refresh = async () => {
    if (!supported()) return;
    try {
      const est = await navigator.storage.estimate();
      setQuotaBytes(est.quota ?? null);
      setUsageBytes(est.usage ?? null);
      if (typeof navigator.storage.persisted === 'function') {
        setPersisted(await navigator.storage.persisted());
      }
    } catch (err) {
      setLastError((err as Error)?.message ?? String(err));
    }
  };

  const requestPersistent = async () => {
    if (!supported() || typeof navigator.storage.persist !== 'function') return false;
    try {
      const ok = await navigator.storage.persist();
      setPersisted(ok);
      return ok;
    } catch (err) {
      setLastError((err as Error)?.message ?? String(err));
      return false;
    }
  };

  onMount(() => {
    void refresh();
  });

  return { supported, quotaBytes, usageBytes, persisted, lastError, refresh, requestPersistent };
}
